import { ITheme } from '@fluentui/react/lib/Styling';
import { memoizeFunction } from '@fluentui/react/lib/Utilities';

import {
  AppColors,
  appNeutralColors,
  controlPalette,
  getPrimaryControlSemanticColorsWithTheme,
} from '@1js/acui-common';
import { isThemingEnabled } from '@1js/acui-theme';

export interface ToggleColors {
  textDisabledColor: string;
  pillCheckedBackground: string;
  pillCheckedHoveredBackground: string;
  pillCheckedDisabledBackground: string;
  thumbBackground: string;
  thumbCheckedBackground: string;
  thumbDisabledBackground: string;
  thumbCheckedDisabledBackground: string;
  pillBorderColor: string;
  pillBorderHoveredColor: string;
  pillBorderDisabledColor: string;
}

export interface PrimaryToggleColors {
  thumbOffBackgroundRest: string;
  thumbOffBackgroundHover: string;
  thumbOffBackgroundPressed: string;
  thumbOffBackgroundDisabled: string;
  thumbBackgroundOn: string;
  pillOffBorderColor: string;
  pillOffBorderColorActive: string;
  pillBorderColorDisabled: string;
  pillOffBackgroundPressed: string;
  pillOffBackgroundDisabled: string;
  pillOnBackgroundRest: string;
  pillOnBackgroundHover: string;
  pillOnBackgroundPressed: string;
  pillOnBackgroundDisabled: string;
  pillOnBorderColorRest: string;
  pillOnBorderColorHover: string;
  pillOnBorderColorPressed: string;
}

// These colors will be deprecated in favor of the themed definitions
// and should no longer be updated or referenced.
const toggleColors: ToggleColors = {
  textDisabledColor: controlPalette.textDisabledColor,
  pillCheckedBackground: controlPalette.pressedBackgroundColor,
  pillCheckedHoveredBackground: controlPalette.selectedBackgroundColor,
  pillCheckedDisabledBackground: controlPalette.textDisabledColor,
  thumbBackground: controlPalette.textColor,
  thumbCheckedBackground: controlPalette.defaultBackgroundColor,
  thumbDisabledBackground: controlPalette.textDisabledColor,
  thumbCheckedDisabledBackground: controlPalette.textDisabledColor,
  pillBorderColor: controlPalette.inputBorderColor,
  pillBorderHoveredColor: controlPalette.inputFocusBorderColor,
  pillBorderDisabledColor: controlPalette.textDisabledColor,
};

// These colors will be deprecated in favor of the themed definitions
// and should no longer be updated or referenced.
const primaryToggleColors = (appColors: AppColors): PrimaryToggleColors => {
  return {
    thumbOffBackgroundRest: appNeutralColors.gray130,
    thumbOffBackgroundHover: appNeutralColors.gray180,
    thumbOffBackgroundPressed: appNeutralColors.gray180,
    thumbOffBackgroundDisabled: appNeutralColors.gray70,
    thumbBackgroundOn: appNeutralColors.white,
    pillOffBorderColor: appNeutralColors.gray110,
    pillOffBorderColorActive: appNeutralColors.gray180,
    pillBorderColorDisabled: appNeutralColors.gray70,
    pillOffBackgroundPressed: appColors.tint40,
    pillOffBackgroundDisabled: appNeutralColors.white,
    pillOnBackgroundRest: appColors.primary,
    pillOnBackgroundHover: appColors.shade10,
    pillOnBackgroundPressed: appColors.shade20,
    pillOnBackgroundDisabled: appNeutralColors.gray70,
    pillOnBorderColorRest: appColors.primary,
    pillOnBorderColorHover: appColors.shade10,
    pillOnBorderColorPressed: appColors.shade20,
  };
};

export const getToggleColorsWithTheme = memoizeFunction(
  (theme: ITheme): ToggleColors => {
    if (!isThemingEnabled()) {
      return toggleColors;
    }
    return {
      textDisabledColor: theme.palette.neutralSecondary,
      pillCheckedBackground: theme.palette.neutralQuaternary,
      pillCheckedHoveredBackground: theme.palette.neutralQuaternaryAlt,
      pillCheckedDisabledBackground: theme.palette.neutralSecondary,
      thumbBackground: theme.palette.neutralPrimary,
      thumbCheckedBackground: theme.palette.neutralLighterAlt,
      thumbDisabledBackground: theme.palette.neutralSecondary,
      thumbCheckedDisabledBackground: theme.palette.neutralSecondary,
      pillBorderColor: theme.palette.neutralQuaternary,
      pillBorderHoveredColor: theme.palette.neutralSecondaryAlt,
      pillBorderDisabledColor: theme.palette.neutralSecondary,
    };
  }
);

export const getPrimaryToggleColorsWithTheme = memoizeFunction(
  (theme: ITheme, appColors: AppColors): PrimaryToggleColors => {
    if (!isThemingEnabled()) {
      return primaryToggleColors(appColors);
    }
    const primaryColors = getPrimaryControlSemanticColorsWithTheme(theme, appColors);
    const restStatePrimaryColor = primaryColors.primaryDefaultBackgroundColor;
    const hoverStatePrimaryColor = primaryColors.primaryHoverBackgroundColor;
    return {
      thumbOffBackgroundRest: theme.palette.neutralPrimaryAlt,
      thumbOffBackgroundHover: theme.palette.neutralDark,
      thumbOffBackgroundPressed: theme.palette.neutralDark,
      thumbOffBackgroundDisabled: theme.palette.neutralTertiaryAlt,
      thumbBackgroundOn: theme.palette.white,
      pillOffBorderColor: theme.palette.neutralSecondaryAlt,
      pillOffBorderColorActive: theme.palette.neutralDark,
      pillBorderColorDisabled: theme.palette.neutralTertiaryAlt,
      pillOffBackgroundPressed: theme.palette.themeLighter,
      pillOffBackgroundDisabled: theme.palette.white,
      pillOnBackgroundRest: restStatePrimaryColor,
      pillOnBackgroundHover: hoverStatePrimaryColor,
      pillOnBackgroundPressed: theme.palette.themeDark,
      pillOnBackgroundDisabled: theme.palette.neutralTertiaryAlt,
      pillOnBorderColorRest: restStatePrimaryColor,
      pillOnBorderColorHover: hoverStatePrimaryColor,
      pillOnBorderColorPressed: theme.palette.themeDark,
    };
  }
);
